<template>
  <div class="unit-status">
    <!-- Top bar -->
    <header class="topbar">
      <button class="brand" @click="backMainPanel" aria-label="Ir al panel principal">
        <img src="@/assets/logogruas.png" alt="Home" />
        <span class="brand__title">Estatus de Unidades</span>
      </button>

      <div class="topbar__right">
        <div class="command">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 4a6 6 0 1 1 3.53 10.86l3.8 3.8-1.41 1.41-3.8-3.8A6 6 0 0 1 10 4m0 2a4 4 0 1 0 .001 8.001A4 4 0 0 0 10 6"/></svg>
          <input v-model.trim="q" placeholder="Buscar unidad / operador" />
        </div>
        <div class="topbar__refresh">
          <button class="chip solid" @click="fetchLocationsAndAssignations" aria-label="Actualizar">Actualizar</button>
          <span class="muted">Auto: 20s</span>
        </div>
      </div>
    </header>

    <!-- Content -->
    <div class="layout">
      <!-- Table / list side -->
      <section class="panel">
        <div class="panel__header">
          <div class="filters">
            <button class="chip" :class="{active: statusFilter==='all'}" @click="statusFilter='all'">Todos</button>
            <button class="chip" :class="{active: statusFilter===1}" @click="statusFilter=1">Disponible</button>
            <button class="chip" :class="{active: statusFilter===2}" @click="statusFilter=2">Ocupada</button>
            <button class="chip" :class="{active: statusFilter===4}" @click="statusFilter=4">En servicio</button>
            <button class="chip" :class="{active: statusFilter===3}" @click="statusFilter=3">Fuera de servicio</button>
            <button class="chip" :class="{active: statusFilter===5}" @click="statusFilter=5">Mantenimiento</button>
          </div>
          <div class="stats">
            <div class="stat"><span class="kpi">{{ locations.length }}</span><span class="lbl">Unidades</span></div>
            <div class="stat" data-tone="ok"><span class="kpi">{{ movingCount }}</span><span class="lbl">En movimiento</span></div>
            <div class="stat" data-tone="warn"><span class="kpi">{{ stoppedCount }}</span><span class="lbl">Detenidas</span></div>
            <div class="stat" data-tone="available"><span class="kpi">{{ availableCount }}</span><span class="lbl">Disponible</span></div>
            <div class="stat" data-tone="busy"><span class="kpi">{{ busyCount }}</span><span class="lbl">Ocupada</span></div>
            <div class="stat" data-tone="service"><span class="kpi">{{ serviceCount }}</span><span class="lbl">En servicio</span></div>
            <div class="kpi-help-wrap">
              <button class="kpi-help-btn" type="button" aria-label="Explicación de KPIs">¿Qué significa?</button>
              <div class="kpi-help-tooltip" role="note">
                <p><strong>Unidades:</strong> Cuenta de unidades activas.</p>
                <p><strong>En movimiento:</strong> Unidades con velocidad diferente de 0.</p>
                <p><strong>Detenidas:</strong> Unidades con velocidad actual de 0.</p>
                <p><strong>Disponible:</strong> Unidades sin ningún servicio asignado.</p>
                <p><strong>Ocupada:</strong> Unidades con 3 o más servicios en cola.</p>
                <p><strong>En servicio:</strong> Unidades realizando algún servicio y con menos de 3 servicios en cola.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="tablewrap">
          <table class="table">
            <thead>
              <tr>
                <th>Estatus</th>
                <th>ID unidad</th>
                <th>Operador actual</th>
                <th>Habilitada</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="location in paginated"
                :key="unitKey(location)"
                :class="{ selected: selectedUnitId === unitKey(location) }"
                @click="selectUnit(location)"
              >
                <td>
                  <span class="chip chip--status" :data-tone="tone(location.unit_status)">{{ statusLabel(location.unit_status) }}</span>
                  <div v-if="!hasCoords(location)" class="gps-bad">DATOS DE GPS INCORRECTOS</div>
                </td>
                <td class="mono">{{ location.unit_short_id }}</td>
                <td @click.stop>
                  <div class="operator-field">
                    <div class="operator-row">
                      <input
                        class="operator-input"
                        :class="{ 'operator-input-disabled': !canAssignOperator(location) }"
                        type="text"
                        :value="operatorInput(location)"
                        :placeholder="canAssignOperator(location) ? 'Asignar operador (nombre o número)' : 'Unidad deshabilitada'"
                        autocomplete="off"
                        :disabled="!canAssignOperator(location)"
                        @input="onOperatorInput(location, $event.target.value, $event.target)"
                        @focus="onOperatorFocus(location, $event.target)"
                        @blur="onOperatorBlur(location)"
                      />
                      <span
                        class="operator-chip-inline"
                        :class="operatorChipClass(location)"
                        :title="operatorChipTitle(location)"
                      >
                        {{ operatorChipLabel(location) }}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <label class="switch" :class="{ disabled: location.unit_status === 5 }" :title="location.unit_status===5 ? maintTip : ''">
                    <input
                      type="checkbox"
                      v-model="location.unit_enabled"
                      @change.stop="toggleUnit(location)"
                      :disabled="location.unit_status===5 || unitToggleInFlight[unitKey(location)]"
                    />
                    <span class="slider"></span>
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination">
          <button class="icon-btn" @click="prevPage" :disabled="currentPage === 1" aria-label="Página anterior">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
          </button>
          <span class="muted">Página {{ currentPage }} de {{ totalPages }}</span>
          <button class="icon-btn" @click="nextPage" :disabled="currentPage >= totalPages" aria-label="Página siguiente">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
          </button>
        </div>
      </section>

      <!-- Map side -->
      <section class="mapwrap">
        <Map ref="mapRef" :center="mapCenter" :zoom="10" map-type-id="roadmap" class="map">
          <Marker
            v-for="location in mapLocations"
            :key="unitKey(location)"
            :position="{ lat: toNum(location.lat), lng: toNum(location.long) }"
            :icon="markerIcon(location)"
            @click="openInfoWindow(location)"
          />
          <InfoWindow :options="{ content: infoContent }" :position="infoPosition" :opened="infoWindowOpened" @closeclick="closeInfoWindow" />
        </Map>
      </section>
    </div>
    <Teleport to="body">
      <ul
        v-if="activeSuggestions.length"
        class="operator-suggestions-overlay"
        :style="operatorDropdownStyle"
      >
        <li
          v-for="employee in activeSuggestions"
          :key="employee.id"
          :class="{ hovered: hoveredSuggestionId === employee.id }"
          @mouseenter="hoveredSuggestionId = employee.id"
          @mouseleave="hoveredSuggestionId = null"
          @mousedown.prevent="selectActiveOperator(employee)"
        >
          {{ employee.employee_ident }} - {{ employee.employee_name }}
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { Map, Marker, InfoWindow } from '@fawmi/vue-google-maps'
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'UnitStatusModern',
  components: { Map, Marker, InfoWindow },
  setup() {
    // --- state
    const locations = ref([])
    const q = ref('')
    const statusFilter = ref('all')

    const mapRef = ref(null)
    const mapCenter = ref({ lat: 25.66831199290133, lng: -100.2802354639708 })
    const infoWindowOpened = ref(false)
    const infoContent = ref('')
    const infoPosition = reactive({ lat: 0, lng: 0 })
    const selectedLocation = ref(null)
    const selectedUnitId = ref(null)

    const currentPage = ref(1)
    const pageSize = ref(20)
    const operatorTextByUnit = ref({})
    const operatorSuggestions = ref({})
    const activeOperatorUnit = ref(null)
    const operatorDropdownStyle = ref({})
    const hoveredSuggestionId = ref(null)
    const pendingOperatorSaveByUnit = ref({})
    const unitEnabledOverrides = ref({})
    const unitToggleInFlight = ref({})
    const operatorSearchTimers = {}
    let fetchSeq = 0
    let latestAppliedFetchSeq = 0

    let intervalId = null

    // --- helpers
    const toNum = (v) => parseFloat(v)
    const maintTip = 'Esa unidad se encuentra en mantenimiento. Para habilitarla, primero cierre la nota de mantenimiento.'
    const unitKey = (u) => u?.unit_short_id || u?.vehicle_plate || u?.vehicle_name || ''
    const hasCoords = (u) => Number.isFinite(toNum(u?.lat)) && Number.isFinite(toNum(u?.long))
    const markerIcon = (u) => ([3, 5].includes(Number(u?.unit_status))
      ? require('@/assets/grua-map-icon-marker-gray.png')
      : require('@/assets/grua-map-icon-marker.png'))

    const STATUS = {
      1: { label: 'Disponible', tone: 'available' },
      2: { label: 'Ocupada', tone: 'busy' },
      3: { label: 'Fuera de servicio', tone: 'down' },
      4: { label: 'En servicio', tone: 'active' },
      5: { label: 'Mantenimiento', tone: 'maint' },
    }
    const statusLabel = (s) => STATUS[s]?.label || String(s)
    const tone = (s) => STATUS[s]?.tone || 'unknown'

    // --- computed
    const filtered = computed(() => {
      const query = q.value.trim().toLowerCase()
      let arr = locations.value
      if (statusFilter.value !== 'all') arr = arr.filter(l => l.unit_status === statusFilter.value)
      if (query) {
        arr = arr.filter(l =>
          String(l.unit_short_id || '').toLowerCase().includes(query) ||
          String(l.current_assigned_operator_name || '').toLowerCase().includes(query) ||
          String(l.current_assigned_operator_ident || '').toLowerCase().includes(query)
        )
      }
      return arr
    })

    const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
    const paginated = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      return filtered.value.slice(start, start + pageSize.value)
    })

    const movingCount = computed(() => locations.value.filter(l => Number(l.vehicle_speed) > 0).length)
    const stoppedCount = computed(() => locations.value.filter(l => Number(l.vehicle_speed) === 0).length)
    const availableCount = computed(() => locations.value.filter(l => Number(l.unit_status) === 1).length)
    const busyCount = computed(() => locations.value.filter(l => Number(l.unit_status) === 2).length)
    const serviceCount = computed(() => locations.value.filter(l => Number(l.unit_status) === 4).length)
    const mapLocations = computed(() => locations.value.filter(hasCoords))
    const activeSuggestions = computed(() =>
      activeOperatorUnit.value ? (operatorSuggestions.value[activeOperatorUnit.value] || []) : []
    )

    // --- actions
    const openInfoWindow = (location) => {
      infoWindowOpened.value = false
      infoContent.value = `
        <div class="gm-info" style="min-width:240px;padding:6px 4px;">
          <h3 style="margin:0 0 6px 0;font-size:15px;font-weight:700;">${location.vehicle_name ?? ''}</h3>
          <div style="font-size:13px;opacity:.8;">Placas: ${location.vehicle_plate ?? ''}</div>
          <div style="font-size:13px;opacity:.8;margin-top:4px;">Velocidad: ${Number(location.vehicle_speed)||0} km/h</div>
        </div>`
      infoPosition.lat = toNum(location.lat)
      infoPosition.lng = toNum(location.long)
      nextTick(() => (infoWindowOpened.value = true))
    }
    const closeInfoWindow = () => {
      infoWindowOpened.value = false
      selectedUnitId.value = null
    }

    const fetchLocationsAndAssignations = async () => {
      const currentFetchSeq = ++fetchSeq
      try {
        const apiDirectory = process.env.API_DIRECTORY
        const { data } = await axios.get(`${apiDirectory}/get-tows-status`)
        // Ignore stale/out-of-order responses (common when toggling many units quickly).
        if (currentFetchSeq < latestAppliedFetchSeq) return
        latestAppliedFetchSeq = currentFetchSeq

        const raw = Array.isArray(data) ? data : []
        locations.value = raw.sort((a, b) => {
          const aKey = String(a?.unit_short_id || a?.vehicle_name || '').toLowerCase()
          const bKey = String(b?.unit_short_id || b?.vehicle_name || '').toLowerCase()
          return aKey.localeCompare(bKey, 'es', { numeric: true })
        })
        locations.value.forEach((location) => {
          const key = unitKey(location)
          if (key in unitEnabledOverrides.value) {
            location.unit_enabled = unitEnabledOverrides.value[key]
          } else {
            location.unit_enabled = isUnitEnabled(location)
          }
        })
        const nextOperatorText = { ...operatorTextByUnit.value }
        locations.value.forEach((location) => {
          const key = unitKey(location)
          if (activeOperatorUnit.value === key || pendingOperatorSaveByUnit.value[key]) return
          if (location.current_assigned_operator_ident && location.current_assigned_operator_name) {
            nextOperatorText[key] = `${location.current_assigned_operator_ident} - ${location.current_assigned_operator_name}`
          } else {
            // Avoid flicker/data-loss on polling: keep existing text for enabled units unless explicitly disabled.
            // Disabled units should always show unassigned.
            nextOperatorText[key] = location.unit_enabled ? (nextOperatorText[key] || '') : ''
          }
        })
        operatorTextByUnit.value = nextOperatorText
        // Keep selection fresh
        if (selectedUnitId.value) {
          const current = locations.value.find(l => unitKey(l) === selectedUnitId.value)
          if (current && hasCoords(current)) selectUnit(current)
        }
      } catch (e) {
        console.error('Error fetching data:', e)
      }
    }

    const selectUnit = (location) => {
      selectedLocation.value = location
      selectedUnitId.value = unitKey(location)
      if (!hasCoords(location)) {
        infoWindowOpened.value = false
        return
      }
      mapCenter.value = { lat: toNum(location.lat), lng: toNum(location.long) }
      openInfoWindow(location)
    }

    const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
    const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
    const operatorInput = (location) => operatorTextByUnit.value[unitKey(location)] || ''
    const operatorChipLabel = (location) => {
      if (location.current_assigned_operator_ident && location.current_assigned_operator_name) {
        return `${location.current_assigned_operator_ident}`
      }
      return 'N/A'
    }
    const operatorChipClass = (location) =>
      location.current_assigned_operator_ident && location.current_assigned_operator_name
        ? 'operator-chip-assigned'
        : 'operator-chip-unassigned'
    const operatorChipTitle = (location) =>
      location.current_assigned_operator_ident && location.current_assigned_operator_name
        ? `Asignado: ${location.current_assigned_operator_ident} - ${location.current_assigned_operator_name}`
        : 'Sin operador asignado'

    const searchOperators = async (location, query) => {
      const key = unitKey(location)
      const trimmed = String(query || '').trim()
      if (!trimmed) {
        operatorSuggestions.value = { ...operatorSuggestions.value, [key]: [] }
        return
      }
      try {
        const apiDirectory = process.env.API_DIRECTORY
        const { data } = await axios.post(`${apiDirectory}/search-status-units-operators`, { query: trimmed })
        operatorSuggestions.value = { ...operatorSuggestions.value, [key]: Array.isArray(data) ? data : [] }
      } catch (error) {
        console.error('Error searching operators:', error)
      }
    }

    const updateDropdownAnchor = (inputEl) => {
      if (!inputEl) return
      const rect = inputEl.getBoundingClientRect()
      operatorDropdownStyle.value = {
        left: `${Math.round(rect.left)}px`,
        top: `${Math.round(rect.bottom + 4)}px`,
        width: `${Math.round(rect.width)}px`,
      }
    }

    const canAssignOperator = (location) => location.unit_enabled === true

    const onOperatorInput = (location, text, inputEl) => {
      if (!canAssignOperator(location)) return
      const key = unitKey(location)
      operatorTextByUnit.value = { ...operatorTextByUnit.value, [key]: text }
      activeOperatorUnit.value = key
      updateDropdownAnchor(inputEl)
      if (operatorSearchTimers[key]) clearTimeout(operatorSearchTimers[key])
      operatorSearchTimers[key] = setTimeout(() => searchOperators(location, text), 200)
    }

    const onOperatorFocus = (location, inputEl) => {
      if (!canAssignOperator(location)) return
      const key = unitKey(location)
      activeOperatorUnit.value = key
      updateDropdownAnchor(inputEl)
      searchOperators(location, operatorInput(location))
    }

    const persistCurrentOperator = async (location, operatorId) => {
      const key = unitKey(location)
      pendingOperatorSaveByUnit.value = { ...pendingOperatorSaveByUnit.value, [key]: true }
      try {
        const apiDirectory = process.env.API_DIRECTORY
        const { data } = await axios.post(`${apiDirectory}/set-status-unit-current-operator`, {
          unit_short_id: location.unit_short_id,
          operator_id: operatorId,
        })
        location.current_assigned_operator = data.current_assigned_operator
        location.current_assigned_operator_ident = data.current_assigned_operator_ident
        location.current_assigned_operator_name = data.current_assigned_operator_name
        operatorTextByUnit.value = {
          ...operatorTextByUnit.value,
          [key]: data.current_assigned_operator_ident && data.current_assigned_operator_name
            ? `${data.current_assigned_operator_ident} - ${data.current_assigned_operator_name}`
            : '',
        }
      } catch (error) {
        console.error('Error saving current operator:', error)
        Swal.fire('¡Error!', 'No se pudo guardar el operador actual.', 'error')
      } finally {
        const nextPending = { ...pendingOperatorSaveByUnit.value }
        delete nextPending[key]
        pendingOperatorSaveByUnit.value = nextPending
      }
    }

    const selectOperator = async (location, employee) => {
      const key = unitKey(location)
      operatorTextByUnit.value = {
        ...operatorTextByUnit.value,
        [key]: `${employee.employee_ident} - ${employee.employee_name}`,
      }
      operatorSuggestions.value = { ...operatorSuggestions.value, [key]: [] }
      activeOperatorUnit.value = null
      hoveredSuggestionId.value = null
      await persistCurrentOperator(location, employee.id)
    }

    const selectActiveOperator = async (employee) => {
      if (!activeOperatorUnit.value) return
      const location = locations.value.find((x) => unitKey(x) === activeOperatorUnit.value)
      if (!location) return
      await selectOperator(location, employee)
    }

    const onOperatorBlur = async (location) => {
      const key = unitKey(location)
      window.setTimeout(async () => {
        operatorSuggestions.value = { ...operatorSuggestions.value, [key]: [] }
        activeOperatorUnit.value = null
        hoveredSuggestionId.value = null
        const currentText = (operatorTextByUnit.value[key] || '').trim()
        if (!currentText && location.current_assigned_operator) {
          await persistCurrentOperator(location, null)
        }
      }, 120)
    }

    const isUnitEnabled = (location) => location.unit_status !== 5 && location.unit_status !== 3

    const toggleUnit = async (location) => {
      const apiDirectory = process.env.API_DIRECTORY
      const key = unitKey(location)
      const desiredEnabled = location.unit_enabled === true
      unitEnabledOverrides.value = { ...unitEnabledOverrides.value, [key]: desiredEnabled }
      unitToggleInFlight.value = { ...unitToggleInFlight.value, [key]: true }
      try {
        activeOperatorUnit.value = null
        hoveredSuggestionId.value = null
        const enabled = desiredEnabled ? 1 : 0
        await axios.post(`${apiDirectory}/enable-disable-unit`, { unit_short_id: location.unit_short_id, enabled })
        if (!location.unit_enabled) {
          location.current_assigned_operator = null
          location.current_assigned_operator_ident = null
          location.current_assigned_operator_name = null
          operatorTextByUnit.value = { ...operatorTextByUnit.value, [key]: '' }
          operatorSuggestions.value = { ...operatorSuggestions.value, [key]: [] }
          if (activeOperatorUnit.value === key) activeOperatorUnit.value = null
        }
        if (location.unit_enabled) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: `Unidad habilitada: ${location.unit_short_id}`,
            showConfirmButton: false,
            timer: 1800,
            timerProgressBar: true,
          })
        } else {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: `Unidad deshabilitada: ${location.unit_short_id}`,
            showConfirmButton: false,
            timer: 1800,
            timerProgressBar: true,
          })
        }
        await fetchLocationsAndAssignations()

        // Release override once backend reflects the desired enabled state.
        const latest = locations.value.find((x) => unitKey(x) === key)
        if (latest && isUnitEnabled(latest) === desiredEnabled) {
          const nextOverrides = { ...unitEnabledOverrides.value }
          delete nextOverrides[key]
          unitEnabledOverrides.value = nextOverrides
        }
      } catch (error) {
        console.error(error)
        const nextOverrides = { ...unitEnabledOverrides.value }
        delete nextOverrides[key]
        unitEnabledOverrides.value = nextOverrides
        location.unit_enabled = !desiredEnabled
        const code = error?.response?.status
        if (code === 500) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: 'Error interno de servidor. Intente nuevamente.',
            showConfirmButton: false,
            timer: 2200,
            timerProgressBar: true,
          })
        } else if (code === 400) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: 'Solicitud inválida. Intente nuevamente.',
            showConfirmButton: false,
            timer: 2200,
            timerProgressBar: true,
          })
        } else {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: 'No se pudo actualizar la unidad.',
            showConfirmButton: false,
            timer: 2200,
            timerProgressBar: true,
          })
        }
      } finally {
        const nextInFlight = { ...unitToggleInFlight.value }
        delete nextInFlight[key]
        unitToggleInFlight.value = nextInFlight
      }
    }

    // --- map resize on layout changes
    const resizeMap = async () => {
      await nextTick()
      const map = await mapRef.value?.$mapPromise
      if (map && window.google?.maps?.event) {
        window.google.maps.event.trigger(map, 'resize')
        map.setCenter(mapCenter.value)
      }
    }

    // --- lifecycle
    onMounted(() => {
      fetchLocationsAndAssignations()
      intervalId = setInterval(fetchLocationsAndAssignations, 30000)
      window.addEventListener('resize', resizeMap)
    })
    onUnmounted(() => {
      if (intervalId) clearInterval(intervalId)
      window.removeEventListener('resize', resizeMap)
    })

    watch([currentPage, pageSize, filtered], () => resizeMap(), { flush: 'post' })

    const backMainPanel = () => (window.location.href = '/main-panel')

    return {
      // state
      locations, q, statusFilter,
      operatorTextByUnit, operatorSuggestions, activeOperatorUnit, operatorDropdownStyle,
      unitToggleInFlight,
      hoveredSuggestionId,
      mapRef, mapCenter, infoWindowOpened, infoContent, infoPosition, selectedLocation, selectedUnitId,
      currentPage, pageSize,
      // computed
      paginated, totalPages, movingCount, stoppedCount, availableCount, busyCount, serviceCount, mapLocations, activeSuggestions,
      // methods
      fetchLocationsAndAssignations, selectUnit, openInfoWindow, closeInfoWindow, nextPage, prevPage, toggleUnit, isUnitEnabled, backMainPanel,
      operatorInput, onOperatorInput, onOperatorFocus, onOperatorBlur, selectOperator, selectActiveOperator,
      canAssignOperator,
      operatorChipClass, operatorChipTitle,
      operatorChipLabel,
      // helpers
      statusLabel, tone, toNum, maintTip, unitKey, hasCoords, markerIcon,
    }
  }
}
</script>

<style>
/* GLOBAL (unscoped) — tokens & resets */
:root{
  --brand:#ffcc2f; --ink:#111827; --bg:#e4e9f1; --panel:#fffaf0;
  --border: rgba(122,87,14,.3); --muted:#6b7280;
  --ok:#16a34a; --warn:#f59e0b; --down:#ef4444; --active:#3b82f6; --maint:#4b5563;
}
*, *::before, *::after { box-sizing: border-box; }
</style>

<style scoped>
.unit-status{ min-height:100vh; background:var(--bg); color:var(--ink); }

/* Topbar */
.topbar{ position:sticky; top:0; z-index:30; height:64px; display:flex; align-items:center; justify-content:space-between; gap:10px; padding:0 12px; background: radial-gradient(1200px 200px at 10% -20%, rgba(255,255,255,.22), transparent 60%), linear-gradient(180deg, var(--brand), #f2b716); box-shadow:0 4px 18px rgba(0,0,0,.18); }
.topbar{ background: radial-gradient(1200px 200px at 10% -20%, rgba(255,255,255,.12), transparent 60%), var(--brand-topbar-bg, #ffc434); }
.brand{ display:flex; align-items:center; gap:10px; background:transparent; border:0; cursor:pointer; }
.brand img{ height:36px; width:auto; }
.brand__title{ font-weight:800; text-transform:uppercase; letter-spacing:.2px; font-size:14px; color:#382a07; }
.topbar__right{ display:flex; align-items:center; gap:10px; }
.command{ display:flex; align-items:center; gap:8px; width:min(42vw,520px); background:rgba(255,255,255,.75); border:1px solid var(--border); border-radius:12px; padding:8px 12px; box-shadow:0 10px 24px rgba(0,0,0,.12); }
.command svg{ width:18px; height:18px; opacity:.6; }
.command input{ flex:1; border:0; background:transparent; outline:none; font-size:14px; padding:4px 2px; color:#0b0e14; }
.topbar__refresh{ display:flex; align-items:center; gap:8px; }
.chip{ font-size:11px; padding:4px 8px; border-radius:16px; border:1px solid rgba(122,87,14,.35); background:rgba(255,247,214,.95); color:#4f3a08; font-weight:700; cursor:pointer; }
.chip.active{ outline:2px solid rgba(29,78,216,.18); background:#1d4ed8; color:#fff; border-color:#1d4ed8; }
.chip.solid{ background:#111827; color:#ffdd66; border-color:#111827; box-shadow:0 4px 10px rgba(0,0,0,.16); }


/* Layout */
.layout{ display:grid; grid-template-columns: 1.1fr 1fr; gap:12px; padding:12px; height: calc(100vh - 64px); }
.panel{ display:flex; flex-direction:column; background:var(--panel); border:1px solid var(--border); border-radius:14px; box-shadow:0 12px 24px rgba(0,0,0,.14); overflow:visible; }
.panel__header{ display:flex; justify-content:space-between; gap:8px; padding:12px; border-bottom:1px solid var(--border); }
.filters{ display:flex; gap:6px; flex-wrap:wrap; }
.stats{ display:flex; gap:6px; flex-wrap:wrap; align-items:center; justify-content:flex-end; }
.stat{ min-width:82px; display:grid; gap:1px; padding:6px 8px; border-radius:10px; background:#ffe9ab; border:1px solid #9a6700; }
.stat[data-tone='ok']{ background:#86efac; border-color:#15803d; }
.stat[data-tone='warn']{ background:#facc15; border-color:#a16207; }
.stat[data-tone='available']{ background:#bbf7d0; border-color:#15803d; }
.stat[data-tone='busy']{ background:#fde68a; border-color:#b45309; }
.stat[data-tone='service']{ background:#bfdbfe; border-color:#1d4ed8; }
.kpi{ font-weight:900; color:#111827; font-size:16px; line-height:1; }
.lbl{ font-size:9px; letter-spacing:.32px; color:#1f2937; text-transform:uppercase; font-weight:800; }
.kpi-help-wrap{ position:relative; margin-left:2px; }
.kpi-help-btn{
  border:1px solid rgba(122,87,14,.35);
  border-radius:999px;
  background:#fff6cf;
  color:#4f3a08;
  font-size:10px;
  font-weight:800;
  padding:5px 8px;
  cursor:help;
}
.kpi-help-tooltip{
  position:absolute;
  right:0;
  top:calc(100% + 8px);
  width:min(420px, 70vw);
  background:#fffef9;
  border:1px solid rgba(122,87,14,.35);
  border-radius:10px;
  box-shadow:0 12px 26px rgba(15,23,42,.2);
  padding:9px 10px;
  opacity:0;
  transform: translateY(6px);
  pointer-events:none;
  transition: opacity .18s ease, transform .18s ease;
  z-index:40;
}
.kpi-help-tooltip p{
  margin:0 0 5px 0;
  font-size:11px;
  color:#1f2937;
  line-height:1.35;
}
.kpi-help-tooltip p:last-child{ margin-bottom:0; }
.kpi-help-wrap:hover .kpi-help-tooltip,
.kpi-help-wrap:focus-within .kpi-help-tooltip{
  opacity:1;
  transform: translateY(0);
}

/* Table */
.tablewrap{
  overflow-x:auto;
  overflow-y:visible;
  position:relative;
}
.table{ width:100%; border-collapse:separate; border-spacing:0; }
.table thead th{ position:sticky; top:0; background:rgba(255,255,255,.96); border-bottom:1px solid var(--border); text-align:left; font-size:12px; padding:10px; z-index:1; }
.table tbody td{ padding:10px; border-bottom:1px solid var(--border); font-size:13px; overflow:visible; background:#fffaf0; }
.table tbody tr{ cursor:pointer; transition: background .15s ease; background:#fffaf0; }
.table tbody tr:nth-child(even) td{ background:#fff6e3; }
.table tbody tr:hover td{ background:#ffefc2; }
.table tbody tr.selected td{ outline:2px solid rgba(29,78,216,.2); background:#dbeafe; }
.mono{ font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
.operator-field{ position:relative; min-width:240px; z-index:5; }
.operator-row{ display:flex; align-items:center; gap:8px; min-height:34px; }
.operator-input{
  width:100%;
  min-width:130px;
  border:1px solid var(--border);
  border-radius:10px;
  padding:8px 10px;
  font-size:12px;
  background:#fff;
}
.operator-input-disabled{
  background:#f3f4f6;
  color:#9ca3af;
  cursor:not-allowed;
}
.operator-suggestions-overlay{
  position:fixed;
  background:#fff;
  border:1px solid var(--border);
  border-radius:10px;
  box-shadow:0 10px 20px rgba(0,0,0,.12);
  list-style:none;
  margin:0;
  padding:4px 0;
  max-height:180px;
  overflow-y:auto;
  z-index:6000;
}
.operator-suggestions-overlay li{
  padding:8px 10px;
  font-size:12px;
  cursor:pointer;
  transition: background-color .12s ease, color .12s ease;
}
.operator-suggestions-overlay li:hover{
  background:#dbeafe;
  color:#1e3a8a;
}
.operator-suggestions-overlay li.hovered{
  background:#dbeafe;
  color:#1e3a8a;
}
.operator-chip-inline{
  display:inline-flex;
  align-items:center;
  font-size:11px;
  font-weight:700;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid var(--border);
  background:#eef2ff;
  color:#1e3a8a;
  white-space:nowrap;
  flex:0 0 auto;
  max-width:120px;
  overflow:hidden;
  text-overflow:ellipsis;
}
.operator-chip-assigned{
  background:#dcfce7;
  color:#166534;
  border-color:#86efac;
}
.operator-chip-unassigned{
  background:#fee2e2;
  color:#991b1b;
  border-color:#fca5a5;
}

/* Status chip */
.chip--status{ font-size:11px; padding:4px 8px; border-radius:999px; border:1px solid var(--border); display:inline-block; }
.chip--status[data-tone="available"]{ background: color-mix(in oklab, var(--ok) 40%, white); }
.chip--status[data-tone="busy"]{ background: color-mix(in oklab, var(--warn) 40%, white); }
.chip--status[data-tone="active"]{ background: color-mix(in oklab, var(--active) 40%, white); }
.chip--status[data-tone="down"]{ background: color-mix(in oklab, var(--down) 40%, white); color: white; }
.chip--status[data-tone="maint"]{ background: color-mix(in oklab, var(--maint) 40%, white); color: #111; }
.gps-bad{ margin-top:4px; font-size:11px; font-weight:700; color:#b91c1c; letter-spacing:.2px; }

/* Switch */
.switch{ position:relative; display:inline-block; width:38px; height:22px; }
.switch input{ opacity:0; width:0; height:0; }
.slider{ position:absolute; cursor:pointer; inset:0; background:#e5e7eb; border:1px solid var(--border); transition:.2s; border-radius:999px; }
.slider::before{ content:""; position:absolute; height:18px; width:18px; left:2px; top:50%; transform: translateY(-50%); background:white; border-radius:50%; box-shadow: 0 1px 4px rgba(0,0,0,.25); transition:.2s; }
.switch input:checked + .slider{ background: color-mix(in oklab, var(--active) 35%, white); }
.switch input:checked + .slider::before{ transform: translate(16px, -50%); }
.switch.disabled{ opacity:.6; cursor:not-allowed; }

/* Pagination */
.pagination{ display:flex; align-items:center; justify-content:center; gap:10px; padding:10px; border-top:1px solid var(--border); background:rgba(255,250,232,.7); }
.icon-btn{ display:grid; place-items:center; width:36px; height:36px; border-radius:10px; border:1px solid rgba(122,87,14,.35); background:#fff6cf; color:#4f3a08; cursor:pointer; }
.icon-btn:disabled{ opacity:.5; cursor:not-allowed; }
.icon-btn svg{ width:20px; height:20px; }

/* Map */
.mapwrap{ background:#cfd8e3; border:1px solid var(--border); border-radius:14px; overflow:hidden; box-shadow:0 12px 24px rgba(0,0,0,.14); }
.map{ width:100%; height:100%; }

/* Infowindow text color */
:deep(.gm-style .gm-style-iw) .gm-info{ color: var(--ink); }

/* Responsive */
@media (max-width: 1200px){
  .layout{ grid-template-columns: 1fr; grid-auto-rows: minmax(300px, auto); }
  .mapwrap{ height: 55vh; }
  .kpi-help-tooltip{ right:auto; left:0; width:min(420px, 88vw); }
}
@media (min-width: 1201px){ .mapwrap{ height: calc(100vh - 88px); } }
</style>
